import { type ReactNode, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FactCheck } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { DataTable } from '../components/data-table';
import {
  type EvalResultFilters,
  EvalResultFiltersBar,
  type TriState,
} from '../components/eval-result-filters';
import { type EvalResultResponse, evalResultsApi } from '../store/eval-results-api';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';

const { useGetV2AdminCortexEvalResultsQuery: useListEvalResults } = evalResultsApi;

type EvalResultColumn = {
  key: keyof EvalResultResponse;
  label: string;
  render: (value: unknown, row: EvalResultResponse) => ReactNode;
};

const triStateToBool = (v: TriState | undefined): boolean | undefined => {
  if (v === 'true') return true;
  if (v === 'false') return false;
  return undefined;
};

const truncate = (value: string | null | undefined, len = 8): string => {
  if (!value) return '—';
  return value.length > len ? `${value.slice(0, len)}…` : value;
};

const EMPTY_EVAL_RESULTS: EvalResultResponse[] = [];

export default function EvalResultsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const page = parseInt(searchParams.get('page') || '1', 10);
  const perPage = parseInt(searchParams.get('per_page') || '20', 10);

  const filters = useMemo<EvalResultFilters>(
    () => ({
      organizationId: searchParams.get('organization_id') || undefined,
      patientId: searchParams.get('patient_id') || undefined,
      feature: searchParams.get('feature') || undefined,
      scorerName: searchParams.get('scorer_name') || undefined,
      source: (searchParams.get('source') as EvalResultFilters['source']) || undefined,
      scorerKind: (searchParams.get('scorer_kind') as EvalResultFilters['scorerKind']) || undefined,
      passed: (searchParams.get('passed') as TriState) || undefined,
      reviewed: (searchParams.get('reviewed') as TriState) || undefined,
      since: searchParams.get('since') || undefined,
      until: searchParams.get('until') || undefined,
    }),
    [searchParams],
  );

  const queryArgs = useMemo(() => {
    const passed = triStateToBool(filters.passed);
    const reviewed = triStateToBool(filters.reviewed);

    return {
      page,
      perPage,
      ...(filters.organizationId && { organizationId: filters.organizationId }),
      ...(filters.patientId && { patientId: filters.patientId }),
      ...(filters.feature && { feature: filters.feature }),
      ...(filters.source && { source: filters.source }),
      ...(filters.scorerKind && { scorerKind: filters.scorerKind }),
      ...(passed !== undefined && { passed }),
      ...(reviewed !== undefined && { reviewed }),
      ...(filters.since && { since: filters.since }),
      ...(filters.until && { until: filters.until }),
    };
  }, [
    page,
    perPage,
    filters.organizationId,
    filters.patientId,
    filters.feature,
    filters.source,
    filters.scorerKind,
    filters.passed,
    filters.reviewed,
    filters.since,
    filters.until,
  ]);

  const { data, isLoading, error, refetch } = useListEvalResults(queryArgs);

  const writeFilters = useCallback(
    (next: EvalResultFilters) => {
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('per_page', String(perPage));
      if (next.organizationId) params.set('organization_id', next.organizationId);
      if (next.patientId) params.set('patient_id', next.patientId);
      if (next.feature) params.set('feature', next.feature);
      if (next.scorerName) params.set('scorer_name', next.scorerName);
      if (next.source) params.set('source', next.source);
      if (next.scorerKind) params.set('scorer_kind', next.scorerKind);
      if (next.passed && next.passed !== 'any') params.set('passed', next.passed);
      if (next.reviewed && next.reviewed !== 'any') params.set('reviewed', next.reviewed);
      if (next.since) params.set('since', next.since);
      if (next.until) params.set('until', next.until);
      setSearchParams(params);
    },
    [perPage, setSearchParams],
  );

  const handleClearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('page', String(newPage));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handlePerPageChange = useCallback(
    (newPerPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('per_page', String(newPerPage));
      params.set('page', '1');
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleRowClick = useCallback(
    (row: EvalResultResponse) => {
      navigate(`/admin-v2/eval-results/${row.eval_result_id}?${searchParams.toString()}`, {
        state: { from: `/admin-v2/eval-results?${searchParams.toString()}` },
      });
    },
    [navigate, searchParams],
  );

  const columns = useMemo<EvalResultColumn[]>(
    () => [
      {
        key: 'created_at',
        label: 'Created',
        render: (value: unknown) =>
          value ? (
            <Box>
              <Typography variant="body2">{formatRelativeTime(value as string)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDateTime(value as string)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        key: 'feature',
        label: 'Feature',
        render: (_value: unknown, row: EvalResultResponse) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {row.feature}
          </Typography>
        ),
      },
      {
        key: 'type_key',
        label: 'Type',
        render: (_value: unknown, row: EvalResultResponse) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {row.type_key}
          </Typography>
        ),
      },
      {
        key: 'scorer_name',
        label: 'Scorer',
        render: (_: unknown, row: EvalResultResponse) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {row.scorer_name}
            </Typography>
            <Chip
              label={row.scorer_kind}
              size="small"
              variant="outlined"
              sx={{ mt: 0.5, textTransform: 'lowercase' }}
            />
          </Box>
        ),
      },
      {
        key: 'passed',
        label: 'Passed',
        render: (_: unknown, row: EvalResultResponse) => (
          <Chip
            label={row.passed ? 'Pass' : 'Fail'}
            size="small"
            color={row.passed ? 'success' : 'error'}
            variant="outlined"
          />
        ),
      },
      {
        key: 'human_verdict',
        label: 'Reviewed',
        render: (_: unknown, row: EvalResultResponse) => {
          if (!row.human_verdict) {
            return <Chip label="Unreviewed" size="small" variant="outlined" color="default" />;
          }
          const color =
            row.human_verdict === 'agree'
              ? 'success'
              : row.human_verdict === 'disagree'
                ? 'error'
                : 'warning';
          return <Chip label={row.human_verdict} size="small" color={color} />;
        },
      },
      {
        key: 'encounter_id',
        label: 'Encounter',
        render: (value: unknown) => (
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {truncate(value as string | null, 12)}
          </Typography>
        ),
      },
      {
        key: 'source',
        label: 'Source',
        render: (_value: unknown, row: EvalResultResponse) => (
          <Chip label={row.source} size="small" variant="outlined" />
        ),
      },
    ],
    [],
  );

  const total = data?.total ?? 0;
  const totalPages = data?.total_pages ?? Math.max(1, Math.ceil(total / perPage));
  const items = data?.items ?? EMPTY_EVAL_RESULTS;

  const visibleItems = useMemo(() => {
    if (!filters.scorerName) return items;
    const needle = filters.scorerName.toLowerCase();
    return items.filter(r => r.scorer_name.toLowerCase().includes(needle));
  }, [items, filters.scorerName]);

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FactCheck fontSize="large" color="primary" />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                Eval Results
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cortex AI scorer outcomes — flat feed across samples
              </Typography>
            </Box>
          </Box>
        </Box>

        <EvalResultFiltersBar
          filters={filters}
          onChange={writeFilters}
          onClear={handleClearFilters}
        />

        {error ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="error" variant="h6" sx={{ mb: 2 }}>
              Failed to load eval results
            </Typography>
            <Button onClick={() => void refetch()} variant="outlined">
              Retry
            </Button>
          </Box>
        ) : (
          <>
            <DataTable<EvalResultResponse>
              title={
                filters.scorerName
                  ? `Results (${visibleItems.length} matching "${filters.scorerName}" on this page · ${items.length} loaded · ${total} total)`
                  : `Results (${items.length} of ${total})`
              }
              columns={columns}
              data={visibleItems}
              total={total}
              loading={isLoading}
              onRowClick={handleRowClick}
              emptyMessage={
                filters.scorerName
                  ? `No rows on this page match scorer name "${filters.scorerName}". Try paginating or clearing this filter — it does not search across pages.`
                  : Object.values(filters).some(v => v !== undefined && v !== 'any')
                    ? 'No eval results match the current filters'
                    : 'No eval results found'
              }
            />

            {total > perPage && (
              <Box
                className="bg-white rounded-lg shadow px-6 py-4 mt-4"
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {Math.min((page - 1) * perPage + 1, total)} to{' '}
                  {Math.min(page * perPage, total)} of {total} results
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small">
                    <InputLabel>Per Page</InputLabel>
                    <Select
                      value={perPage}
                      label="Per Page"
                      onChange={e => handlePerPageChange(e.target.value)}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={page <= 1}
                      onClick={() => handlePageChange(page - 1)}
                    >
                      Previous
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Page {page} of {totalPages}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={page >= totalPages}
                      onClick={() => handlePageChange(page + 1)}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </main>
    </div>
  );
}
